import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Crown, ChevronRight, Target, AlertTriangle, CheckCircle2, Clock,
  Users, BarChart3, PieChart, Sparkles, Bookmark, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TierBadge, ConfidenceBadge } from '../components/TierBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchAuth = async (url) => {
  const token = localStorage.getItem('backoffice_token');
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

const ActionSummaryCard = ({ label, value, onClick, testId, accent = 'text-[#0A1F44]' }) => (
  <div
    onClick={onClick}
    data-testid={testId}
    className="bg-white rounded-xl p-3.5 border border-gray-200 cursor-pointer hover:shadow-md hover:border-[#D4AF37]/50 transition-all text-center"
  >
    <p className={`text-2xl font-bold ${accent}`}>{value ?? 0}</p>
    <p className="text-[11px] text-gray-500 font-medium mt-0.5">{label}</p>
  </div>
);

const timeAgo = (iso) => {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
};

const DailyBriefingHome = ({ embedded = false, initialView = 'today' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(initialView === 'week' ? 'week' : 'today');
  const navigate = useNavigate();

  const load = (v) => {
    setLoading(true);
    fetchAuth(`${API_URL}/api/intelligence/daily-briefing?view=${v}`)
      .then(setData).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(view); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const goto = (path) => navigate(path);

  if (loading && !data) return (
    <div className={embedded ? "flex items-center justify-center py-24" : "min-h-screen bg-gray-50 flex items-center justify-center"}>
      <div className="flex items-center gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Building your daily briefing...</div>
    </div>
  );

  const {
    action_summary = {}, action_required = [], priority_accounts_preview = [],
    priority_accounts_total = 0, execution = {}, acquisition_funnel = {},
    new_intelligence = {}, freshness = {},
  } = data || {};

  const actionableItems = action_required.filter(a => a.count > 0);
  const hasNoAction = actionableItems.length === 0;

  return (
    <div className={embedded ? "" : "min-h-screen bg-gray-50"}>
      {!embedded && (
        <div className="bg-[#0A1F44] text-white">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <div className="flex items-center gap-3">
              <Crown className="h-6 w-6 text-[#D4AF37]" />
              <h1 className="text-xl font-bold" data-testid="daily-briefing-title">Daily Briefing</h1>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-5 space-y-4" data-testid="daily-briefing-root">
        {/* Toggle + freshness */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1 w-fit" data-testid="daily-briefing-period-toggle">
            <button
              onClick={() => setView('today')}
              data-testid="period-toggle-today"
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'today' ? 'bg-[#0A1F44] text-white' : 'text-gray-500 hover:text-[#0A1F44]'}`}
            >
              Today
            </button>
            <button
              onClick={() => setView('week')}
              data-testid="period-toggle-week"
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'week' ? 'bg-[#0A1F44] text-white' : 'text-gray-500 hover:text-[#0A1F44]'}`}
            >
              This Week
            </button>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-gray-400" data-testid="daily-briefing-freshness">
            <RefreshCw className="h-3 w-3" />
            Last refreshed: {timeAgo(freshness.last_refreshed)}
            <button onClick={() => load(view)} className="text-[#0A1F44] hover:underline font-medium" data-testid="daily-briefing-refresh-btn">Refresh</button>
          </div>
        </div>

        {freshness.stale_outreach_warning && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-lg px-3 py-2" data-testid="stale-outreach-warning">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {freshness.stale_outreach_warning}
          </div>
        )}

        {/* A. Action Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="action-summary-row">
          <ActionSummaryCard label="Priority Accounts" value={action_summary.priority_accounts}
            onClick={() => goto('/backoffice/intelligence/opportunity-priority')} testId="summary-priority-accounts" accent="text-emerald-700" />
          <ActionSummaryCard label="Needs Enrichment" value={action_summary.needs_enrichment}
            onClick={() => goto('/backoffice/intelligence/enrich-queue')} testId="summary-needs-enrichment" accent="text-amber-700" />
          <ActionSummaryCard label="Follow-Ups Due" value={action_summary.followups_due}
            onClick={() => goto('/backoffice/intelligence/command-center?tab=recruiter-activity')} testId="summary-followups-due" />
          <ActionSummaryCard label="Responses / Conversations" value={action_summary.responses_conversations}
            onClick={() => goto('/backoffice/intelligence/command-center?tab=client-acquisition')} testId="summary-responses" />
          <ActionSummaryCard label="New High-Value Signals" value={action_summary.new_high_value_signals}
            onClick={() => goto('/backoffice/intelligence/signal-feeds')} testId="summary-new-signals" accent="text-orange-600" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left column */}
          <div className="space-y-4">
            {/* B. Action Required */}
            <Card data-testid="action-required-card">
              <CardHeader className="bg-red-600 text-white rounded-t-lg py-2.5 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5" /> Action Required</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {hasNoAction ? (
                  <div className="px-4 py-5 text-center" data-testid="action-required-empty">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto mb-1.5" />
                    <p className="text-sm text-gray-500">No follow-ups or responses due today.</p>
                    <button onClick={() => goto('/backoffice/intelligence/opportunity-priority')} className="text-xs text-[#0A1F44] hover:underline mt-1">
                      Go to Priority Accounts / Enrichment Queue
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {actionableItems.map((item, i) => (
                      <div key={item.type} onClick={() => goto(item.link)}
                        className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 cursor-pointer"
                        data-testid={`action-item-${item.type}`}>
                        <span className="text-sm text-gray-700 flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-gray-400" /> {item.label}
                        </span>
                        <span className="flex items-center gap-1 text-sm font-bold text-red-600">
                          {item.count} <ChevronRight className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* C. Today's Priority Accounts */}
            <Card data-testid="priority-accounts-preview-card">
              <CardHeader className="bg-[#D4AF37] text-[#0A1F44] rounded-t-lg py-2.5 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2"><Target className="h-3.5 w-3.5" /> Today's Priority Accounts</CardTitle>
                  <button onClick={() => goto('/backoffice/intelligence/opportunity-priority')} className="text-xs hover:underline flex items-center gap-0.5" data-testid="view-all-priority-accounts">
                    View all {priority_accounts_total.toLocaleString()} <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-gray-100 max-h-[280px] overflow-y-auto">
                  {priority_accounts_preview.length === 0 && (
                    <p className="px-4 py-6 text-center text-gray-400 text-sm">No priority accounts available</p>
                  )}
                  {priority_accounts_preview.map((c, i) => (
                    <div key={i} className="flex items-start gap-2 px-4 py-2.5 hover:bg-[#fdf8ef] cursor-pointer"
                      onClick={() => goto('/backoffice/intelligence/opportunity-priority')}
                      data-testid={`priority-preview-row-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-semibold text-[#0A1F44] truncate">{c.company}</p>
                          <TierBadge tier={c.priority_tier} />
                          <ConfidenceBadge confidence={c.firmographic_confidence} flags={c.confidence_flags} />
                          {c.on_watchlist && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] text-indigo-600 font-medium" data-testid={`watchlist-flag-${i}`}>
                              <Bookmark className="h-2.5 w-2.5" /> Watchlist
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-gray-500 truncate mt-0.5">{c.best_niche || 'Broad target'} — {c.why_text}</p>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-gray-300 shrink-0 mt-1" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column */}
          <div className="space-y-4">
            {/* D. Today's Execution */}
            <Card data-testid="execution-summary-card">
              <CardHeader className="bg-indigo-600 text-white rounded-t-lg py-2.5 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2"><BarChart3 className="h-3.5 w-3.5" /> {view === 'today' ? "Today's" : "This Week's"} Execution</CardTitle>
                  <button onClick={() => goto('/backoffice/intelligence/command-center?tab=recruiter-activity')} className="text-xs hover:underline flex items-center gap-0.5">
                    View Recruiter Activity <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="p-3">
                {Object.values(execution).every(v => !v) ? (
                  <p className="text-center text-sm text-gray-400 py-2" data-testid="execution-empty">
                    Nothing logged yet {view === 'today' ? 'today' : 'this week'}. Start with Priority Accounts or Enrichment Queue.
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Researched', value: execution.companies_researched },
                      { label: 'DMs Found', value: execution.decision_makers_identified },
                      { label: 'Outreach', value: execution.outreach_attempts },
                      { label: 'Follow-ups', value: execution.followups_scheduled },
                      { label: 'Responses', value: execution.responses_received },
                      { label: 'Meetings', value: execution.meetings_scheduled },
                    ].map((s, i) => (
                      <div key={i} className="text-center p-2 bg-gray-50 rounded-lg border border-gray-100">
                        <p className="text-base font-bold text-[#0A1F44]">{s.value || 0}</p>
                        <p className="text-[9px] text-gray-500">{s.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* E. Client Acquisition snapshot */}
            <Card data-testid="acquisition-snapshot-card">
              <CardHeader className="bg-[#0A1F44] text-white rounded-t-lg py-2.5 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2"><PieChart className="h-3.5 w-3.5" /> Client Acquisition</CardTitle>
                  <button onClick={() => goto('/backoffice/intelligence/command-center?tab=client-acquisition')} className="text-xs hover:underline flex items-center gap-0.5 text-[#D4AF37]">
                    View Full Funnel <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="p-3">
                <div className="flex items-center justify-between text-center" data-testid="acquisition-funnel-compact">
                  {[
                    { label: 'Identified', value: acquisition_funnel.identified },
                    { label: 'Contacted', value: acquisition_funnel.contacted },
                    { label: 'Responses', value: acquisition_funnel.responses },
                    { label: 'Conversations', value: acquisition_funnel.conversations },
                    { label: 'Opportunities', value: acquisition_funnel.opportunities },
                    { label: 'Clients', value: acquisition_funnel.clients },
                  ].map((s, i, arr) => (
                    <React.Fragment key={i}>
                      <div>
                        <p className="text-sm font-bold text-[#0A1F44]">{(s.value || 0).toLocaleString()}</p>
                        <p className="text-[9px] text-gray-500">{s.label}</p>
                      </div>
                      {i < arr.length - 1 && <ChevronRight className="h-3 w-3 text-gray-300" />}
                    </React.Fragment>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* F. New Intelligence */}
            <Card data-testid="new-intelligence-card">
              <CardHeader className="bg-orange-600 text-white rounded-t-lg py-2.5 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> New Intelligence</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-gray-100">
                  <div className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 cursor-pointer" onClick={() => goto('/backoffice/intelligence/signal-feeds')} data-testid="new-intel-companies">
                    <span className="text-sm text-gray-700">New/first-time hiring companies</span>
                    <span className="flex items-center gap-1 text-sm font-bold text-orange-600">{new_intelligence.new_companies || 0} <ChevronRight className="h-3.5 w-3.5" /></span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 cursor-pointer" onClick={() => goto('/backoffice/intelligence/signal-feeds')} data-testid="new-intel-surge">
                    <span className="text-sm text-gray-700">Hiring momentum / surge signals</span>
                    <span className="flex items-center gap-1 text-sm font-bold text-orange-600">{new_intelligence.surge_signals || 0} <ChevronRight className="h-3.5 w-3.5" /></span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 cursor-pointer" onClick={() => goto('/backoffice/intelligence/talent-shortage')} data-testid="new-intel-shortage">
                    <span className="text-sm text-gray-700">Major talent-shortage signals</span>
                    <span className="flex items-center gap-1 text-sm font-bold text-orange-600">{new_intelligence.shortage_signals || 0} <ChevronRight className="h-3.5 w-3.5" /></span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 cursor-pointer" onClick={() => goto('/backoffice/intelligence/geo-heatmap')} data-testid="new-intel-geo">
                    <span className="text-sm text-gray-700">Regional hotspots</span>
                    <span className="flex items-center gap-1 text-xs font-medium text-gray-400">Geo Heatmap <ChevronRight className="h-3.5 w-3.5" /></span>
                  </div>
                </div>
                {new_intelligence.new_companies === 0 && new_intelligence.surge_signals === 0 && new_intelligence.shortage_signals === 0 && (
                  <p className="px-4 py-2 text-center text-[11px] text-gray-400" data-testid="new-intel-empty">No material new signals since the last refresh.</p>
                )}
              </CardContent>
            </Card>

            <button onClick={() => goto('/backoffice/intelligence/command-center?tab=executive-snapshot')}
              className="text-xs text-gray-400 hover:text-[#0A1F44] hover:underline flex items-center gap-1 justify-end w-full" data-testid="view-executive-snapshot-link">
              View Executive Snapshot <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyBriefingHome;
